import {
    IConditionHolder,
    IControlConditionRule,
    IConditionItem,
    IAppState,
    IAppStateControlProperty,
    IFormControl,
    IFieldSetSettings,
} from '../types';

/** @private */
const isEmpty = (value: any) => String(value) === '' || value === null || value === undefined;

/** @private */
const processPredicate = (
    controlContainer: IAppStateControlProperty,
    ruleItem: IConditionItem,
) => {
    const fieldToCompare = controlContainer[ruleItem.field];

    if (!fieldToCompare) throw new Error('No field in processPredicate');

    switch (ruleItem.rule) {
        case 'equal':
            return String(fieldToCompare.value) === String(ruleItem.value);

        case 'not equal':
            return String(fieldToCompare.value) !== String(ruleItem.value);

        case 'is empty':
            return isEmpty(fieldToCompare.value);

        case 'is not empty':
            return !isEmpty(fieldToCompare.value);

        default:
            return true;
    }
};

/** @private */
const processAction = (
    ctrl: IConditionHolder,
    conditionItem: IControlConditionRule,
    predicate: boolean,
) => {
    let changed = false;
    const control = ctrl;
    switch (conditionItem.action) {
        case 'hide':
            changed = control.visible === predicate;

            if (predicate && control.value !== undefined) {
                control.value = undefined;
            }

            control.disabled = predicate;
            control.visible = !predicate;
            break;

        case 'show':
            changed = control.visible !== predicate;
            control.disabled = !predicate;
            control.visible = predicate;
            break;

        case 'enable':
            changed = control.disabled === predicate;
            control.disabled = !predicate;
            break;

        case 'disable':
            changed = control.disabled !== predicate;

            if (predicate && control.value !== undefined) {
                control.value = undefined;
            }

            control.disabled = predicate;
            break;

        default:
            break;
    }

    return changed;
};

/** @private */
export const applyConditionToState = (
    control: IFormControl | IFieldSetSettings,
    appState: IAppState,
    condition: IControlConditionRule,
) => {
    const fn: (callback: (value: any, index: number, array: any[]) => unknown) => boolean = condition.when === 'all' ? Array.prototype.every : Array.prototype.some;

    try {
        const predicate: boolean = fn.call(condition.items, (rule: IConditionItem) =>
            // Always passing "appState.controlsState" here and never "appState.fieldSetState", since the control name's conditions are the ones that act as triggers in all cases.
            processPredicate(appState.controlsState, rule),
        );

        return processAction(control, condition, predicate);
    } catch (e) {
        return false;
    }
};

export const checkConditionalControls = (appState: IAppState, source: any) => {
    return appState.conditionalControlNames.reduce((sourceAsArg, controlName) => {
        const control = appState.controlsState[controlName];
        const stateToReturn = sourceAsArg;
        control.condition!.forEach((condition) => {
            if (applyConditionToState(control, appState, condition)) {
                stateToReturn[control.controlName] = { $set: { ...control } };
            }
        });

        return stateToReturn;
    }, source);
};

export const checkConditionalFieldSets = (appState: IAppState, source: any) => {
    return appState.conditionalFieldSets.reduce((sourceAsArg, fieldSetName) => {
        const fieldSet = appState.fieldSetState[fieldSetName];
        const stateToReturn = sourceAsArg;
        fieldSet.condition!.forEach((condition) => {
            if (applyConditionToState(fieldSet, appState, condition)) {
                stateToReturn[fieldSet.fieldSetName] = { $set: { ...fieldSet } };
            }
        });

        return stateToReturn;
    }, source);
};
